import z, { ZodType } from "zod";

import type { UUID } from "@thalamos/common";
import { looseUuid } from "@thalamos/common";

import {
  OrganisationConfigurationJsonSchema,
  type OrganisationConfigurationJsonUnbranded,
} from "./configuration.js";
import { EmailSchema } from "./email.js";
import {
  OrganisationIdSchema,
  UserOrganisationMembershipSchema,
  UserOrganisationRoleSchema,
  type OrganisationIdUnbranded,
  type UserOrganisationMembershipUnbranded,
  type UserOrganisationRole,
} from "./organisation.js";

/**
 * An ID for a User
 */
export type UserIdUnbranded = UUID;
export const UserIdSchema =
  looseUuid().brand<"vision-common:UserId">() satisfies ZodType<UserIdUnbranded>;
export type UserId = z.infer<typeof UserIdSchema>;

/**
 * A User
 */
export type UserUnbranded = {
  id: UserIdUnbranded;
  name: string;
  email: string;
  shoulderNumber?: string;
  samAccountName?: string;
};

export const UserSchema = z
  .object({
    id: UserIdSchema,
    name: z.string().min(1).max(255),
    email: EmailSchema,
    shoulderNumber: z.string().max(255).optional(),
    samAccountName: z.string().max(255).optional(),
  })
  .brand<"vision-common:User">() satisfies ZodType<UserUnbranded>;

export type User = z.infer<typeof UserSchema>;

export type ExtendedVisionUserUnbranded = UserUnbranded & {
  memberships: UserOrganisationMembershipUnbranded[];
  sessionContext: UserOrganisationMembershipUnbranded | null;
  sessionOrganisationConfiguration?: OrganisationConfigurationJsonUnbranded;
  isOnboarded: boolean;
  supervisorSyncExemption: boolean;
  supervisorSyncExemptionDate: string | null;
};

export const ExtendedVisionUserSchema = z
  .object({
    id: UserIdSchema,
    name: z.string().max(255),
    email: EmailSchema,
    shoulderNumber: z.string().max(255).optional(),
    isAdminSession: z.boolean(),
    sessionContext: UserOrganisationMembershipSchema.nullable(),
    sessionOrganisationConfiguration:
      OrganisationConfigurationJsonSchema.optional(),
    memberships: z.array(UserOrganisationMembershipSchema),
    isOnboarded: z.boolean(),
    samAccountName: z.string().min(1).max(255).optional(),
    supervisorSyncExemption: z.boolean().default(false),
    supervisorSyncExemptionDate: z.string().nullable().default(null),
  })
  .brand<"vision-common:ExtendedVisionUser">() satisfies ZodType<ExtendedVisionUserUnbranded>;

export type ExtendedVisionUser = z.infer<typeof ExtendedVisionUserSchema>;

export type CreateUserUnbranded = UserUnbranded & {
  organisationId: OrganisationIdUnbranded;
};

export const CreateUserSchema = z
  .object({
    id: UserIdSchema,
    name: z.string().min(1).max(255),
    email: EmailSchema,
    shoulderNumber: z.string().max(255).optional(),
    organisationId: OrganisationIdSchema,
    samAccountName: z.string().max(255).optional(),
  })
  .brand<"vision-common:CreateUser">() satisfies ZodType<CreateUserUnbranded>;

export type CreateUser = z.infer<typeof CreateUserSchema>;

export type UserUpdateRequestUnbranded = {
  organisationId?: OrganisationIdUnbranded;
  name?: string;
  isOnboarded?: boolean;
  shoulderNumber?: string | null;
  samAccountName?: string;
};

export const UserUpdateRequestSchema = z.object({
  organisationId: OrganisationIdSchema.optional(),
  name: z.string().optional(),
  isOnboarded: z.boolean().optional(),
  shoulderNumber: z.string().nullable().optional(),
  samAccountName: z.string().optional(),
});

export type UserUpdateRequest = z.infer<typeof UserUpdateRequestSchema>;

export type OnboardUserRequestUnbranded = {
  name: string;
  currentOrganisationId: OrganisationIdUnbranded;
  organisationId: OrganisationIdUnbranded;
  role: UserOrganisationRole;
};

export const OnboardUserRequestSchema = z.object({
  name: z.string().min(1).max(255),
  currentOrganisationId: OrganisationIdSchema,
  organisationId: OrganisationIdSchema,
  role: UserOrganisationRoleSchema,
});

export type OnboardUserRequest = z.infer<typeof OnboardUserRequestSchema>;

export type ExternalUser = {
  name: string;
  upn: string;
  mail: string;
  id: string;
  samAccountName?: string;
  shoulderNumber?: string | null;
  supervisor?: boolean;
  commandUnit?: string;
};

export const ExternalUserSchema = z.object({
  name: z.string(),
  upn: z.string(),
  mail: EmailSchema,
  id: z.string(),
  samAccountName: z.string().optional(),
  shoulderNumber: z.string().nullable().optional(),
  supervisor: z.boolean().optional(),
  commandUnit: z.string().optional(),
});

///

export type SearchUsersResponseUnbranded = {
  results: UserUnbranded[];
  count: number;
};

export const SearchUsersResponseSchema = z
  .object({
    results: z.array(UserSchema),
    count: z.number(),
  })
  .brand<"vision-common:SearchUsersResponse">();

export type SearchUsersResponse =
  z.infer<typeof SearchUsersResponseSchema> extends SearchUsersResponseUnbranded
    ? z.infer<typeof SearchUsersResponseSchema>
    : never;

export type SearchExtendedUsersResponseUnbranded = {
  results: ExtendedVisionUserUnbranded[];
  count: number;
};
export const SearchExtendedUsersResponseSchema = z
  .object({
    results: z.array(ExtendedVisionUserSchema),
    count: z.number(),
  })
  .brand<"vision-common:SearchExtendedUsersResponse">();
export type SearchExtendedUsersResponse =
  z.infer<
    typeof SearchExtendedUsersResponseSchema
  > extends SearchExtendedUsersResponseUnbranded
    ? z.infer<typeof SearchExtendedUsersResponseSchema>
    : never;

///

export type SetSupervisorSyncExemptionRequestUnbranded = {
  exempt: boolean;
};

export const SetSupervisorSyncExemptionRequestSchema = z.object({
  exempt: z.boolean(),
});

export type SetSupervisorSyncExemptionRequest = z.infer<
  typeof SetSupervisorSyncExemptionRequestSchema
>;
